<template>
  <main class="rh-page">
    <rh-cabecalho
      pagina="estrutura"
      subtitulo="ESTRUTURA"
      titulo="Áreas e equipes"
      descricao="Organize as áreas, equipes e seus membros na empresa selecionada."
      :unidade-id="unidadeId"
      :empresas="empresas"
      :mostrar-pessoas="podeGerir || isInRole('rh-lider')"
      :mostrar-configuracao="podeGerir"
      @empresa-selecionada="selecionarEmpresa"
    />

    <b-message v-if="erro" type="is-danger" aria-close-label="Fechar" @close="erro = ''">{{ erro }}</b-message>
    <b-loading :active="loading" :is-full-page="false"></b-loading>
    <div v-if="!loading && !empresas.length" class="rh-empty">
      Nenhuma empresa vinculada ao seu acesso. Peça ao administrador para configurar sua unidade.
    </div>

    <template v-if="unidadeId">
      <div class="rh-grid">
        <section class="rh-card">
          <div class="rh-section-head">
            <div>
              <p class="rh-eyebrow">ESTRUTURA</p>
              <h2>Áreas</h2>
            </div>
            <b-button v-if="podeGerir" size="is-small" type="is-primary" icon-left="plus" @click="abrirArea"
              >Nova área</b-button
            >
          </div>
          <p class="rh-muted">Agrupe as equipes pelas áreas da empresa.</p>
          <b-table :data="areas" :loading="loading" paginated :per-page="8" hoverable mobile-cards>
            <b-table-column v-slot="props" field="nome" label="Nome" sortable>{{ props.row.nome }}</b-table-column>
            <b-table-column v-if="podeGerir" v-slot="props" label="Ações" width="96">
              <b-button
                size="is-small"
                type="is-light"
                icon-left="pencil"
                :aria-label="'Editar ' + props.row.nome"
                @click="abrirArea(props.row)"
              ></b-button>
            </b-table-column>
            <template #empty><div class="rh-empty">Nenhuma área cadastrada nesta empresa.</div></template>
          </b-table>
        </section>

        <section class="rh-card">
          <div class="rh-section-head">
            <div>
              <p class="rh-eyebrow">PESSOAS</p>
              <h2>Equipes</h2>
            </div>
            <b-button
              v-if="podeGerir"
              size="is-small"
              type="is-primary"
              icon-left="plus"
              :disabled="!areas.length"
              @click="abrirEquipe"
              >Nova equipe</b-button
            >
          </div>
          <p class="rh-muted">Defina o líder e vincule colaboradores a cada equipe.</p>
          <b-table :data="equipes" :loading="loading" paginated :per-page="8" hoverable mobile-cards>
            <b-table-column v-slot="props" field="nome" label="Equipe" sortable>{{ props.row.nome }}</b-table-column>
            <b-table-column v-slot="props" field="area" label="Área" sortable>{{ props.row.area }}</b-table-column>
            <b-table-column v-slot="props" field="lider" label="Líder"
              ><b-tag :type="props.row.lider ? 'is-success' : 'is-warning'">{{
                props.row.lider || 'Pendente'
              }}</b-tag></b-table-column
            >
            <b-table-column v-if="podeGerir || isInRole('rh-lider')" v-slot="props" label="Ações" width="132">
              <b-button
                size="is-small"
                type="is-light"
                icon-left="account-group"
                :aria-label="'Membros de ' + props.row.nome"
                @click="abrirMembros(props.row)"
              ></b-button>
              <b-button
                v-if="podeGerir"
                size="is-small"
                type="is-light"
                icon-left="pencil"
                :aria-label="'Editar ' + props.row.nome"
                @click="abrirEquipe(props.row)"
              ></b-button>
            </b-table-column>
            <template #empty
              ><div class="rh-empty">Nenhuma equipe cadastrada ou disponível para seu perfil.</div></template
            >
          </b-table>
        </section>
      </div>
    </template>

    <b-modal :active.sync="modalArea" has-modal-card trap-focus aria-role="dialog" aria-modal>
      <ValidationObserver v-slot="{ handleSubmit }" slim
        ><form class="modal-card" @submit.prevent="handleSubmit(salvarArea)">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ area.id ? 'Editar área' : 'Nova área' }}</p>
          </header>
          <section class="modal-card-body">
            <ValidationProvider v-slot="{ errors }" name="Nome da área" rules="required|min:2|max:120">
              <b-field label="Nome da área" :type="errors.length ? 'is-danger' : ''" :message="errors[0]"
                ><b-input v-model.trim="area.nome" maxlength="120" expanded></b-input
              ></b-field>
            </ValidationProvider>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="modalArea = false">Cancelar</b-button
            ><b-button native-type="submit" type="is-primary" :loading="salvando">Salvar</b-button>
          </footer>
        </form></ValidationObserver
      >
    </b-modal>

    <b-modal :active.sync="modalEquipe" has-modal-card trap-focus aria-role="dialog" aria-modal>
      <ValidationObserver v-slot="{ handleSubmit }" slim
        ><form class="modal-card" @submit.prevent="handleSubmit(salvarEquipe)">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ equipe.id ? 'Editar equipe' : 'Nova equipe' }}</p>
          </header>
          <section class="modal-card-body">
            <ValidationProvider v-slot="{ errors }" name="Nome da equipe" rules="required|min:2|max:120"
              ><b-field label="Nome da equipe" :type="errors.length ? 'is-danger' : ''" :message="errors[0]"
                ><b-input v-model.trim="equipe.nome" maxlength="120"></b-input></b-field
            ></ValidationProvider>
            <ValidationProvider v-slot="{ errors }" name="Área" rules="required"
              ><b-field label="Área" :type="errors.length ? 'is-danger' : ''" :message="errors[0]"
                ><b-select v-model="equipe.areaId" expanded placeholder="Selecione uma área"
                  ><option v-for="item in areas" :key="item.id" :value="item.id">{{ item.nome }}</option></b-select
                ></b-field
              ></ValidationProvider
            >
            <b-field label="Líder"
              ><b-select v-model="equipe.liderId" expanded placeholder="Selecione um líder"
                ><option :value="null">Definir depois</option>
                <option v-for="item in lideres" :key="item.id" :value="item.id">{{ item.nome }}</option></b-select
              ></b-field
            >
            <p class="rh-muted">
              Você pode preparar a equipe antes de cadastrar ou convidar o líder. Atribua um líder com perfil MV Líder
              antes de abrir o diagnóstico.
            </p>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="modalEquipe = false">Cancelar</b-button
            ><b-button native-type="submit" type="is-primary" :loading="salvando">Salvar</b-button>
          </footer>
        </form></ValidationObserver
      >
    </b-modal>

    <b-modal :active.sync="modalMembros" has-modal-card trap-focus aria-role="dialog" aria-modal>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ equipe.nome }} · membros</p>
        </header>
        <section class="modal-card-body">
          <b-message v-if="erro" type="is-danger" aria-close-label="Fechar" @close="erro = ''">{{ erro }}</b-message>
          <b-field v-if="podeGerir || isInRole('rh-lider')" label="Adicionar colaborador">
            <b-select v-model="funcionarioSelecionado" expanded placeholder="Selecione uma pessoa"
              ><option v-for="item in funcionariosDisponiveis" :key="item.id" :value="item.id">
                {{ item.nome }} · {{ item.email }}
              </option></b-select
            >
            <p class="control">
              <b-button type="is-primary" :disabled="!funcionarioSelecionado" :loading="salvando" @click="vincular"
                >Adicionar</b-button
              >
            </p>
          </b-field>
          <p v-if="!funcionariosDisponiveis.length" class="rh-muted">
            Nenhuma pessoa sem vínculo ativo está disponível para adicionar a esta equipe.
          </p>
          <b-table :data="membros" paginated :per-page="8" mobile-cards>
            <b-table-column v-slot="props" field="nome" label="Colaborador">{{ props.row.nome }}</b-table-column>
            <b-table-column v-slot="props" field="email" label="E-mail">{{ props.row.email || '—' }}</b-table-column>
            <b-table-column v-slot="props" label="Ações" width="80"
              ><b-button
                size="is-small"
                type="is-danger"
                icon-left="close"
                :aria-label="'Remover ' + props.row.nome"
                @click="desvincular(props.row)"
              ></b-button
            ></b-table-column>
            <template #empty><div class="rh-empty">Ainda não há membros nesta equipe.</div></template>
          </b-table>
        </section>
        <footer class="modal-card-foot"><b-button @click="modalMembros = false">Fechar</b-button></footer>
      </div>
    </b-modal>
  </main>
</template>

<script>
import http from '@/http'
import RhCabecalho from '@/components/rh_cabecalho.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'RhEstrutura',
  components: { RhCabecalho },
  data() {
    return {
      empresas: [],
      unidadeId: null,
      areas: [],
      equipes: [],
      funcionarios: [],
      membros: [],
      area: { id: null, nome: '' },
      equipe: { id: null, nome: '', areaId: null, liderId: null },
      funcionarioSelecionado: null,
      modalArea: false,
      modalEquipe: false,
      modalMembros: false,
      loading: false,
      salvando: false,
      erro: '',
    }
  },
  computed: {
    ...mapGetters(['isInRole']),
    podeGerir() {
      return this.isInRole('rh-diretoria')
    },
    funcionariosDisponiveis() {
      return this.funcionarios.filter((x) => !x.vinculado && !this.membros.some((m) => m.id === x.id))
    },
    lideres() {
      return this.funcionarios.filter((x) => x.lider)
    },
  },
  created() {
    this.iniciar()
  },
  methods: {
    selecionarEmpresa(id) {
      this.unidadeId = id
      this.carregar()
    },
    falha(e) {
      this.erro = e && e.data && e.data.erro ? e.data.erro : 'Não foi possível concluir a operação. Tente novamente.'
    },
    async iniciar() {
      this.loading = true
      try {
        this.empresas = (await http.get('/api/rh/estrutura/empresas')).data || []
        const atual = Number(this.$store.state.unidadeId)
        this.unidadeId = this.empresas.some((x) => x.id === atual)
          ? atual
          : (this.empresas[0] && this.empresas[0].id) || null
        if (this.unidadeId) await this.carregar()
      } catch (e) {
        this.falha(e)
      } finally {
        this.loading = false
      }
    },
    async carregar() {
      if (!this.unidadeId) return
      this.loading = true
      this.erro = ''
      try {
        const q = { params: { unidadeId: this.unidadeId } }
        const [areas, equipes] = await Promise.all([
          http.get('/api/rh/estrutura/areas', q),
          http.get('/api/rh/estrutura/equipes', q),
        ])
        this.areas = areas.data || []
        this.equipes = equipes.data || []
        if (this.podeGerir || this.isInRole('rh-lider'))
          this.funcionarios = (await http.get('/api/rh/estrutura/funcionarios', q)).data || []
      } catch (e) {
        this.falha(e)
      } finally {
        this.loading = false
      }
    },
    abrirArea(item) {
      this.area = item ? { id: item.id, nome: item.nome } : { id: null, nome: '' }
      this.modalArea = true
    },
    abrirEquipe(item) {
      this.equipe = item
        ? { id: item.id, nome: item.nome, areaId: item.areaId, liderId: item.liderId }
        : { id: null, nome: '', areaId: null, liderId: null }
      this.modalEquipe = true
    },
    async salvarArea() {
      this.salvando = true
      try {
        await http.post('/api/rh/estrutura/areas', { ...this.area, unidadeId: this.unidadeId })
        this.modalArea = false
        await this.carregar()
        this.$buefy.toast.open({ message: 'Área salva.', type: 'is-success' })
      } catch (e) {
        this.falha(e)
      } finally {
        this.salvando = false
      }
    },
    async salvarEquipe() {
      this.salvando = true
      try {
        await http.post('/api/rh/estrutura/equipes', { ...this.equipe, unidadeId: this.unidadeId })
        this.modalEquipe = false
        await this.carregar()
        this.$buefy.toast.open({ message: 'Equipe salva.', type: 'is-success' })
      } catch (e) {
        this.falha(e)
      } finally {
        this.salvando = false
      }
    },
    async abrirMembros(item) {
      this.erro = ''
      this.equipe = item
      this.funcionarioSelecionado = null
      this.modalMembros = true
      await this.carregarMembros()
    },
    async carregarMembros() {
      try {
        this.membros =
          (
            await http.get('/api/rh/estrutura/membros', {
              params: { unidadeId: this.unidadeId, equipeId: this.equipe.id },
            })
          ).data || []
      } catch (e) {
        this.falha(e)
      }
    },
    async vincular() {
      this.salvando = true
      try {
        await http.post('/api/rh/estrutura/membros', {
          unidadeId: this.unidadeId,
          equipeId: this.equipe.id,
          funcionarioId: this.funcionarioSelecionado,
        })
        this.funcionarioSelecionado = null
        await this.carregarMembros()
        await this.carregar()
        this.$buefy.toast.open({ message: 'Colaborador adicionado.', type: 'is-success' })
      } catch (e) {
        this.falha(e)
      } finally {
        this.salvando = false
      }
    },
    desvincular(item) {
      this.$buefy.dialog.confirm({
        title: 'Remover da equipe',
        message: `Remover ${item.nome} desta equipe?`,
        confirmText: 'Remover',
        cancelText: 'Cancelar',
        type: 'is-danger',
        onConfirm: async () => {
          try {
            await http.delete('/api/rh/estrutura/membros', {
              params: { unidadeId: this.unidadeId, equipeId: this.equipe.id, funcionarioId: item.id },
            })
            await this.carregarMembros()
            await this.carregar()
            this.$buefy.toast.open({ message: 'Colaborador removido.', type: 'is-success' })
          } catch (e) {
            this.falha(e)
          }
        },
      })
    },
  },
}
</script>

<style scoped>
.rh-page {
  --rh-navy: #0b1a3e;
  --rh-blue: #007bff;
  --rh-teal: #0e8fa8;
  --rh-paper: #f4f3f0;
  --rh-ink: #1c1d24;
  background: var(--rh-paper);
  color: var(--rh-ink);
  font-family: Montserrat, Arial, sans-serif;
  min-height: 100vh;
  padding: 0 28px 48px;
  border-radius: 18px;
}
.rh-eyebrow {
  font-size: 11px;
  letter-spacing: 0.16em;
  font-weight: 800;
  color: var(--rh-teal);
  margin: 0;
}
.rh-grid {
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 18px;
}
.rh-card {
  background: #fff;
  border: 1px solid #e7e9ed;
  border-radius: 17px;
  padding: 24px;
  min-width: 0;
}
.rh-section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.rh-section-head h2 {
  font-size: 22px;
  color: var(--rh-navy);
  font-weight: 800;
  margin: 5px 0 0;
}
.rh-muted {
  font-size: 13px;
  color: #667085;
  margin: 8px 0 20px;
}
.rh-empty {
  padding: 28px 12px;
  text-align: center;
  color: #667085;
  font-size: 14px;
}
@media (max-width: 900px) {
  .rh-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 600px) {
  .rh-page {
    padding: 0 14px 28px;
  }
  .rh-card {
    padding: 18px;
  }
  .rh-section-head h2 {
    font-size: 20px;
  }
}
</style>
